import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Observable, throwError } from "rxjs";

// The values of FT.AutoTrader's Domain/Alerts enums: the Admin API sends enums as numbers.
export enum AlertSeverity {
    Critical = 1,
    Warning = 2,
}

export enum AlertState {
    Open = 1,
    Acknowledged = 2,
    Ignored = 3,
    Resolved = 4,
}

export enum AlertActionSource {
    Telegram = 1,
    AdminPage = 2,
}

export enum AlertChannel {
    Telegram = 1,
}

export enum AlertAction {
    Acknowledge = 0,
    Resolve = 1,
    Ignore = 2,
}

export enum AlertDeliveryStatus {
    Delivered = 0,
    NotDelivered = 1,
    DestinationSwitchedOff = 2,
    DestinationRemoved = 3,
    BelowMinimumSeverity = 4,
}

export const ALERT_SEVERITY_LABELS: Record<AlertSeverity, string> = {
    [AlertSeverity.Critical]: "Critical",
    [AlertSeverity.Warning]: "Warning",
};

export const ALERT_STATE_LABELS: Record<AlertState, string> = {
    [AlertState.Open]: "Open",
    [AlertState.Acknowledged]: "Acknowledged",
    [AlertState.Ignored]: "Ignored",
    [AlertState.Resolved]: "Resolved",
};

export const ALERT_ACTION_SOURCE_LABELS: Record<AlertActionSource, string> = {
    [AlertActionSource.Telegram]: "Telegram",
    [AlertActionSource.AdminPage]: "Admin page",
};

export const ALERT_CHANNEL_LABELS: Record<AlertChannel, string> = {
    [AlertChannel.Telegram]: "Telegram",
};

export const ALERT_ACTION_LABELS: Record<AlertAction, string> = {
    [AlertAction.Acknowledge]: "Acknowledge",
    [AlertAction.Resolve]: "Resolve",
    [AlertAction.Ignore]: "Ignore",
};

export const ALERT_DELIVERY_STATUS_LABELS: Record<AlertDeliveryStatus, string> = {
    [AlertDeliveryStatus.Delivered]: "Delivered",
    [AlertDeliveryStatus.NotDelivered]: "Not delivered",
    [AlertDeliveryStatus.DestinationSwitchedOff]: "Destination switched off",
    [AlertDeliveryStatus.DestinationRemoved]: "Destination removed",
    [AlertDeliveryStatus.BelowMinimumSeverity]: "Below the destination's minimum severity",
};

// A value this build does not know is shown as unknown, never as another value.
export function labelOf<T extends number>(labels: Record<T, string>, value: T): string {
    return labels[value] ?? `Unknown (${value})`;
}

const ACTION_ROUTES: Record<AlertAction, string> = {
    [AlertAction.Acknowledge]: "acknowledge",
    [AlertAction.Resolve]: "resolve",
    [AlertAction.Ignore]: "ignore",
};

// An action the API may send that this build does not know is neither offered nor posted.
export function isSupportedAction(action: AlertAction): boolean {
    return Object.hasOwn(ACTION_ROUTES, action);
}

@Injectable({ providedIn: "root" })
export class AlertsService {
    private readonly http = inject(HttpClient);

    list(page: number): Observable<ListActiveAlertsResponse> {
        return this.http.get<ListActiveAlertsResponse>("/alerts", { params: { page } });
    }

    // The id comes from the address, so it is encoded: "../users/…" must not reach another Admin API route.
    get(id: string): Observable<AlertView> {
        return this.http.get<AlertView>(`/alerts/${encodeURIComponent(id)}`);
    }

    submitAction(id: string, action: AlertAction, comment: string | null): Observable<void> {
        if (!isSupportedAction(action)) {
            // Through the observable, so a caller's error path and finalize run as for any failed request.
            return throwError(() => new Error(`Unsupported alert action ${action}`));
        }

        const body: AlertActionRequest = { comment };
        return this.http.post<void>(`/alerts/${encodeURIComponent(id)}/${ACTION_ROUTES[action]}`, body);
    }
}

export type AlertActionRequest = {
    comment: string | null;
};

// source is null only for a quiet Warning's automatic resolution, whose "by" is "automatically".
export type AlertActionView = {
    by: string;
    source: AlertActionSource | null;
    at: string;
    comment: string | null;
};

export type AlertDeliveryView = {
    destinationKey: string;
    destinationName: string | null;
    channel: AlertChannel;
    status: AlertDeliveryStatus;
    deliveredAt: string | null;
    attempts: number;
    lastError: string | null;
    originalAddress: string | null;
    address: string | null;
};

// actions: what the alert's state allows, by the rule the API refuses by.
// typeName: the type's name as the API defines it, so a new alert type needs no website release; type is its value.
export type AlertView = {
    id: string;
    severity: AlertSeverity;
    type: number;
    typeName: string;
    subject: string;
    text: string;
    lastText: string;
    count: number;
    raisedAt: string;
    lastSeenAt: string;
    state: AlertState;
    acknowledged: AlertActionView | null;
    ignored: AlertActionView | null;
    resolved: AlertActionView | null;
    actions: AlertAction[];
    deliveries: AlertDeliveryView[];
};

// The three counts stop at countCap.
export type ListActiveAlertsResponse = {
    items: AlertView[];
    total: number;
    pageSize: number;
    unownedCritical: number;
    activeWithoutDelivery: number;
    undelivered: number;
    countCap: number;
};
